import type { HeroAsset, IconEdge } from './layout';
import { DEFAULT_MOTION_ORIGIN } from './motion';

export type IconReveal = {
	startMs: number;
	durationMs: number;
};

export type EdgeReveal = {
	startMs: number;
	durationMs: number;
	fromAssetId: string;
};

export type IntroSchedule = {
	icons: Map<string, IconReveal>;
	edges: Map<string, EdgeReveal>;
	totalMs: number;
};

export type IntroSchedulingCfg = {
	avatarStaggerMs: number;
	edgeGrowMs: number;
	iconAppearMs: number;
};

export type IntroCfg = IntroSchedulingCfg & {
	iconAppearOvershoot: number;
};

export function easeOutBack(t: number, overshoot: number = 1.7): number {
	if (t <= 0) return 0;
	if (t >= 1) return 1;
	const c1 = overshoot;
	const c3 = c1 + 1;
	const x = t - 1;
	return 1 + c3 * x * x * x + c1 * x * x;
}

function makeEdgeId(edge: IconEdge, index: number): string {
	return `${edge.from}__${edge.to}__${index}`;
}

function dist2(ax: number, ay: number, bx: number, by: number): number {
	const dx = ax - bx;
	const dy = ay - by;
	return dx * dx + dy * dy;
}

export function computeIntroSchedule(
	assets: readonly HeroAsset[],
	edges: readonly IconEdge[],
	cfg: IntroSchedulingCfg,
): IntroSchedule {
	const icons = new Map<string, IconReveal>();
	const edgeReveals = new Map<string, EdgeReveal>();

	const avatars = assets.filter((asset) => asset.kind === 'avatar');
	avatars.sort((a, b) => {
		const da = dist2(a.x, a.y, DEFAULT_MOTION_ORIGIN.x, DEFAULT_MOTION_ORIGIN.y);
		const db = dist2(b.x, b.y, DEFAULT_MOTION_ORIGIN.x, DEFAULT_MOTION_ORIGIN.y);
		return da - db;
	});

	let totalMs = 0;
	for (let i = 0; i < avatars.length; i++) {
		const startMs = i * cfg.avatarStaggerMs;
		icons.set(avatars[i].id, { startMs, durationMs: cfg.iconAppearMs });
		totalMs = Math.max(totalMs, startMs + cfg.iconAppearMs);
	}

	type Incident = { edgeId: string; otherId: string };
	const adjacency = new Map<string, Incident[]>();
	for (const asset of assets) adjacency.set(asset.id, []);
	for (let i = 0; i < edges.length; i++) {
		const edge = edges[i];
		const id = makeEdgeId(edge, i);
		adjacency.get(edge.from)?.push({ edgeId: id, otherId: edge.to });
		adjacency.get(edge.to)?.push({ edgeId: id, otherId: edge.from });
	}

	const queue: { id: string; readyMs: number }[] = [];
	for (const avatar of avatars) {
		const reveal = icons.get(avatar.id);
		if (reveal) queue.push({ id: avatar.id, readyMs: reveal.startMs + reveal.durationMs });
	}

	const assetIndex = new Map<string, number>();
	for (let i = 0; i < assets.length; i++) assetIndex.set(assets[i].id, i);
	const jitterFor = (id: string): number => {
		const idx = assetIndex.get(id) ?? 0;
		return ((idx * 2654435761) >>> 0) % 60;
	};

	let head = 0;
	while (head < queue.length) {
		const { id: parentId, readyMs } = queue[head++];
		const incidents = adjacency.get(parentId);
		if (!incidents) continue;

		for (const incident of incidents) {
			if (edgeReveals.has(incident.edgeId)) continue;

			const edgeStart = readyMs + jitterFor(incident.edgeId);
			edgeReveals.set(incident.edgeId, {
				startMs: edgeStart,
				durationMs: cfg.edgeGrowMs,
				fromAssetId: parentId,
			});
			totalMs = Math.max(totalMs, edgeStart + cfg.edgeGrowMs);

			if (!icons.has(incident.otherId)) {
				const iconStart = edgeStart + cfg.edgeGrowMs * 0.6;
				icons.set(incident.otherId, { startMs: iconStart, durationMs: cfg.iconAppearMs });
				totalMs = Math.max(totalMs, iconStart + cfg.iconAppearMs);
				queue.push({ id: incident.otherId, readyMs: iconStart + cfg.iconAppearMs });
			}
		}
	}

	if (avatars.length > 0) {
		for (const asset of assets) {
			if (icons.has(asset.id)) continue;

			let bestAvatar = avatars[0];
			let bestDist = dist2(asset.x, asset.y, bestAvatar.x, bestAvatar.y);
			for (let i = 1; i < avatars.length; i++) {
				const distance = dist2(asset.x, asset.y, avatars[i].x, avatars[i].y);
				if (distance < bestDist) {
					bestDist = distance;
					bestAvatar = avatars[i];
				}
			}
			const anchor = icons.get(bestAvatar.id);
			const delayMs = Math.sqrt(bestDist) * 600;
			const startMs = (anchor?.startMs ?? 0) + (anchor?.durationMs ?? 0) + delayMs;
			icons.set(asset.id, { startMs, durationMs: cfg.iconAppearMs });
			totalMs = Math.max(totalMs, startMs + cfg.iconAppearMs);
		}
	} else {
		for (const asset of assets) icons.set(asset.id, { startMs: 0, durationMs: cfg.iconAppearMs });
		totalMs = Math.max(totalMs, cfg.iconAppearMs);
	}

	return { icons, edges: edgeReveals, totalMs };
}
